// client/src/dnd/views/Interactive_parts/DemoParts.js
import React, { useState, useEffect } from "react";

function DemoParts({ hardcodedMarker, setHardcodedMarker, isMobile, toolboxBottom }) {
  const [isRaining, setIsRaining] = useState(false);
  const [isWallDemoOn, setIsWallDemoOn] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window._paRainOn) {
        setIsRaining(true);
      }
    } catch {}

    try {
      if (window._paDemoWallsOn) {
        setIsWallDemoOn(true);
      }
    } catch {}
  }, []);

  // ─────────────────────────────────────
  // DEMO ONLY: disable sidebar collapse in demo mode
  // (hardcodedMarker present ⇒ demo running)
  // ─────────────────────────────────────
  useEffect(() => {
    if (!hardcodedMarker) return;
    if (typeof document === "undefined") return;

    function disableSidebarToggleButtons() {
      try {
        const buttons = document.querySelectorAll("button");
        buttons.forEach((btn) => {
          const aria = (btn.getAttribute("aria-label") || "").toLowerCase();
          const title = (btn.getAttribute("title") || "").toLowerCase();
          const cls = (btn.className || "").toString().toLowerCase();

          // Try to catch the little sidebar collapse pill without nuking everything
          const looksLikeSidebarToggle =
            aria.includes("sidebar") ||
            aria.includes("collapse") ||
            title.includes("sidebar") ||
            title.includes("collapse") ||
            cls.includes("sidebar-toggle");

          if (looksLikeSidebarToggle) {
            btn.disabled = true;
            btn.style.pointerEvents = "none";
            btn.style.opacity = "0.4";
          }
        });
      } catch {}
    }

    // run once now, then a few times in case the button mounts later
    disableSidebarToggleButtons();
    const intervalId = setInterval(disableSidebarToggleButtons, 1000);

    return () => clearInterval(intervalId);
  }, [hardcodedMarker]);

  function handleRevealSecretsClick() {
    if (typeof window === "undefined") return;
    try {
      window.dispatchEvent(new Event("pointatlas-demo-reveal-secrets"));
    } catch {}
  }

  function handleRainClick() {
    if (typeof window === "undefined") return;

    const next = !isRaining;

    try {
      window.dispatchEvent(
        new CustomEvent("pointatlas-demo-toggle-rain", {
          detail: {
            raining: next,
          },
        })
      );
    } catch {}

    setIsRaining(next);
    try {
      window._paRainOn = next;
    } catch {}
  }

  function handleWallsDemoClick() {
    if (typeof window === "undefined") return;

    const next = !isWallDemoOn;

    try {
      window.dispatchEvent(
        new CustomEvent("pointatlas-demo-toggle-walls", {
          detail: {
            enabled: next,
          },
        })
      );
    } catch {}

    setIsWallDemoOn(next);
    try {
      window._paDemoWallsOn = next;
    } catch {}
  }

  // run hooks first, *then* bail out if we don't have a marker
  if (!hardcodedMarker) return null;

  return (
    <div
      style={{
        position: isMobile ? "fixed" : "absolute",
        left: "50%",
        bottom: isMobile ? toolboxBottom : 24,
        transform: "translateX(-50%)",
        zIndex: 22150,
        background: "#222f",
        padding: "8px 20px 12px",
        borderRadius: 16,
        boxShadow: "0 2px 14px #000b",
        width: isMobile ? "90%" : 360,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        color: "#fff",
        fontWeight: "600",
        fontSize: 14,
        userSelect: "none",
        textAlign: "center",
      }}
    >
      {/* Dark sight label */}
      <label
        htmlFor="hardcoded-reach-slider"
        style={{ marginBottom: 2 }}
      >
        Dark Sight Range: {hardcodedMarker.reach} meters
      </label>

      {/* Slider directly under the label */}
      <input
        id="hardcoded-reach-slider"
        type="range"
        min={1}
        max={100}
        step={1}
        value={hardcodedMarker.reach}
        onChange={(e) =>
          setHardcodedMarker((m) =>
            m ? { ...m, reach: Number(e.target.value) } : m
          )
        }
        style={{
          marginTop: 2,
          marginBottom: 4,
          width: "100%",
        }}
      />

      {/* Row of buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Make it rain button */}
        <button
          type="button"
          onClick={handleRainClick}
          style={{
            background: isRaining ? "#38bdf8" : "#4b5563",
            color: "#111",
            border: "none",
            borderRadius: 999,
            padding: "6px 10px",
            fontWeight: 700,
            fontSize: 12,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {isRaining ? "Stop Rain" : "Make it rain"}
        </button>

        {/* Demo wall editor toggle – non-owners can draw walls, never saved */}
        <button
          type="button"
          onClick={handleWallsDemoClick}
          style={{
            background: isWallDemoOn ? "#4ade80" : "#4b5563",
            color: "#111",
            border: "none",
            borderRadius: 999,
            padding: "6px 10px",
            fontWeight: 700,
            fontSize: 12,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {isWallDemoOn ? "Stop Wall Edit" : "Draw Walls"}
        </button>

        <button
          type="button"
          onClick={handleRevealSecretsClick}
          style={{
            background: "#b29132",
            color: "#111",
            border: "none",
            borderRadius: 999,
            padding: "6px 14px",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          Reveal Secrets
        </button>
      </div>

      {/* Patreon CTA at the very bottom */}
      <a
        href="https://patreon.com/point_atlas?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "6px 12px",
          borderRadius: 999,
          background: "#fbbf24",
          color: "#111",
          fontWeight: 700,
          fontSize: 12,
          textDecoration: "none",
          marginTop: 6,
          boxShadow: "0 1px 6px #000a",
          whiteSpace: "nowrap",
        }}
      >
        Enjoying the demo? Support Point Atlas on Patreon
      </a>
    </div>
  );
}

export default DemoParts;
